import React from "react";
import { QRCodeSVG } from "qrcode.react";
import { format } from "date-fns";

export default function StudentIdCard({ student }) {
  if (!student) return null;

  // Assuming student is an object containing all ID card details.
  // Generate verification URL
  // We use window.location.origin to point to the current tenant's domain,
  // e.g., https://institute.skillzy.pro/verify/student/12345
  const verifyUrl = `${window.location.origin}/verify/student/${student.id}`;

  const defaultPhoto = "https://ui-avatars.com/api/?name=" + encodeURIComponent(student.full_name || "Student") + "&background=random";

  return (
    <div className="flex flex-col md:flex-row gap-8 justify-center items-center font-['Inter',sans-serif] text-slate-900 w-full max-w-4xl mx-auto p-4 bg-transparent print:p-0">
      
      {/* Front Side */}
      <div className="w-[3.375in] h-[2.125in] bg-white rounded-xl shadow-2xl relative overflow-hidden flex flex-row border border-slate-200 print:shadow-none print:break-inside-avoid print:border-slate-300 shrink-0 id-card-front">
        {/* Background Accent */}
        <div className="absolute top-0 right-0 w-32 h-full bg-blue-600 shrink-0 clip-path-slant z-0"></div>

        {/* Left Side Content */}
        <div className="flex flex-col justify-between p-3 relative z-10 w-2/3 h-full">
          {/* Header / Logo */}
          <div className="flex items-center gap-2">
            {student.institute_logo ? (
              <img src={student.institute_logo} alt="Logo" className="h-8 w-8 object-contain" />
            ) : (
              <div className="h-8 w-8 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center font-black text-xs">
                {student.institute_name ? student.institute_name.substring(0, 2).toUpperCase() : "IN"}
              </div>
            )}
            <h2 className="text-[10px] font-black leading-tight uppercase line-clamp-2">
              {student.institute_name || "SKILLZY INSTITUTE"}
            </h2>
          </div>

          {/* Student Details */}
          <div className="mt-2">
            <h1 className="text-sm font-black text-blue-700 uppercase tracking-tight line-clamp-1">{student.full_name}</h1>
            <p className="text-[8px] font-bold text-slate-500 uppercase tracking-widest">{student.course_name || "General Program"}</p>
            
            <div className="grid grid-cols-2 gap-x-2 gap-y-1 mt-2 text-[8px] font-medium text-slate-600">
              <div><span className="font-bold text-slate-800">ID NO:</span> {student.id}</div>
              <div><span className="font-bold text-slate-800">ROLL NO:</span> {student.roll_number || "N/A"}</div>
              <div><span className="font-bold text-slate-800">DOB:</span> {student.dob || "N/A"}</div>
              <div><span className="font-bold text-slate-800">BLOOD:</span> <span className="text-red-600 font-black">{student.blood_group || "N/A"}</span></div>
              <div className="col-span-2"><span className="font-bold text-slate-800">PHONE:</span> {student.mobile_no || "N/A"}</div>
            </div>
          </div>
          
          {/* Validity */}
          <div className="mt-auto pt-1 flex justify-between items-end border-t border-slate-100">
             <div className="text-[7px] font-bold uppercase text-slate-400">
                Valid Until: <span className="text-slate-800">{student.id_card_valid_until ? format(new Date(student.id_card_valid_until), "MMM dd, yyyy") : "Lifetime"}</span>
             </div>
          </div>
        </div>

        {/* Right Side Photo & QR */}
        <div className="w-1/3 h-full relative z-10 flex flex-col items-center justify-center py-2 px-1 gap-2 border-l-2 border-white/20">
          <div className="w-16 h-20 bg-white p-0.5 rounded shadow-sm border border-slate-100">
             <img src={student.profile_picture || defaultPhoto} alt={student.full_name} className="w-full h-full object-cover rounded-sm grayscale-[20%]" />
          </div>
          <div className="bg-white p-1 rounded border border-slate-100 shadow-sm mt-1">
             <QRCodeSVG value={verifyUrl} size={40} level="H" />
          </div>
        </div>

      </div>

      {/* Back Side */}
      <div className="w-[3.375in] h-[2.125in] bg-slate-50 rounded-xl shadow-2xl relative overflow-hidden flex flex-col p-3 border border-slate-200 print:shadow-none print:break-inside-avoid print:border-slate-300 shrink-0 id-card-back">
        
        {/* Header Bar */}
        <div className="w-full bg-slate-900 py-1 px-2 absolute top-0 left-0 text-center">
            <span className="text-[8px] font-black text-white uppercase tracking-[0.2em]">Contact & Emergency Info</span>
        </div>

        <div className="mt-6 flex-1 text-[8px] text-slate-700 flex flex-col gap-1.5 leading-tight">
           <div className="grid grid-cols-[80px_1fr] border-b border-slate-200 pb-1">
             <span className="font-bold">Father's Name:</span>
             <span>{student.father_name || "N/A"}</span>
           </div>
           <div className="grid grid-cols-[80px_1fr] border-b border-slate-200 pb-1">
             <span className="font-bold">Mother's Name:</span>
             <span>{student.mother_name || "N/A"}</span>
           </div>
           <div className="grid grid-cols-[80px_1fr] border-b border-slate-200 pb-1">
             <span className="font-bold text-red-600">Emergency No:</span>
             <span className="font-black">{student.emergency_contact || "N/A"}</span>
           </div>
           <div className="grid grid-cols-[80px_1fr] border-b border-slate-200 pb-1">
             <span className="font-bold">Address:</span>
             <span className="line-clamp-2">{student.address || "N/A"}</span>
           </div>
           
           <div className="mt-auto border-t-2 border-dashed border-slate-300 pt-1 text-[6px] text-center text-slate-500 flex justify-between items-end">
              <div className="text-left w-2/3">
                 <strong>Terms:</strong> This card is the property of the institute. If found, please return to the registered address. Scan QR on front to verify.
              </div>
              <div className="text-right w-1/3 flex flex-col items-end gap-1">
                 <div className="h-4 w-12 border-b border-slate-800"></div>
                 <span className="font-bold uppercase tracking-wider">Authorized Sig.</span>
              </div>
           </div>
        </div>
      </div>
      
      <style dangerouslySetInnerHTML={{__html: `
        .clip-path-slant {
           clip-path: polygon(20% 0, 100% 0, 100% 100%, 0% 100%);
        }
        @media print {
          @page { size: auto; margin: 0mm; }
          body { 
            background: white; 
            margin: 0; 
            padding: 0;
            display: flex;
            flex-direction: column;
            align-items: center;
          }
          .id-card-front, .id-card-back {
            break-inside: avoid;
            page-break-inside: avoid;
            margin-bottom: 2mm;
          }
        }
      `}} />
    </div>
  );
}
